import React from "react";
import { useApp } from "@/context/AppContext";
import { motion, AnimatePresence } from "framer-motion";
import { Timer, X } from "lucide-react";

const PRESETS = [
  { label: "45s", seconds: 45 },
  { label: "1 min", seconds: 60 },
  { label: "90s", seconds: 90 },
  { label: "2 min", seconds: 120 },
  { label: "3 min", seconds: 180 },
];

export function RestTimer() {
  const { restTimer, cancelRestTimer, startRestTimer } = useApp();
  const { active, remaining, total } = restTimer;

  const pct = total > 0 ? remaining / total : 0;
  const radius = 34;
  const circ = 2 * Math.PI * radius;
  const dash = pct * circ;

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}:${sec.toString().padStart(2, "0")}` : `${sec}s`;
  };

  const almostDone = remaining <= 10 && remaining > 0;

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          initial={{ opacity: 0, y: 80, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 80, scale: 0.92 }}
          transition={{ type: "spring", damping: 22, stiffness: 320 }}
          // Centrado con left/right + mx-auto, no con -translate-x-1/2:
          // framer-motion escribe transform en línea al animar y anularía la
          // clase de Tailwind, dejando el panel fuera de pantalla.
          className="fixed bottom-[76px] left-0 right-0 mx-auto z-50 w-full max-w-xs px-4"
        >
          <div
            className="rounded-3xl p-4 border border-border/40"
            style={{
              background: "hsl(var(--card) / 0.92)",
              backdropFilter: "blur(24px)",
              boxShadow: "0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)",
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <Timer className="h-3.5 w-3.5 text-primary" />
                <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                  Descanso
                </span>
              </div>
              <button
                onClick={cancelRestTimer}
                className="h-6 w-6 rounded-full flex items-center justify-center text-muted-foreground/60 hover:text-foreground hover:bg-secondary/60 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Timer display */}
            <div className="flex items-center gap-4 mb-3">
              {/* Ring */}
              <div className="relative flex-shrink-0">
                <svg width="80" height="80">
                  <circle cx="40" cy="40" r={radius} fill="none"
                    stroke="hsl(var(--border))" strokeWidth="5" />
                  <circle cx="40" cy="40" r={radius} fill="none"
                    stroke={almostDone ? "hsl(var(--destructive))" : "hsl(var(--primary))"}
                    strokeWidth="5" strokeLinecap="round"
                    strokeDasharray={`${dash} ${circ}`}
                    transform="rotate(-90 40 40)"
                    style={{ transition: "stroke-dasharray 1s linear, stroke 0.4s" }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`font-bold tabular-nums text-lg leading-none ${almostDone ? "text-destructive" : "text-foreground"}`}>
                    {formatTime(remaining)}
                  </span>
                </div>
              </div>

              {/* Presets */}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Cambiar duración
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {PRESETS.map((p) => (
                    <button
                      key={p.seconds}
                      onClick={() => startRestTimer(p.seconds)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors duration-150 ${
                        total === p.seconds
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Skip button */}
            <button
              onClick={cancelRestTimer}
              className="w-full h-10 rounded-2xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-colors"
            >
              Listo
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
